import axios, { AxiosInstance } from 'axios';
import * as TokenService from '@src/network/token';
import { useAuthStore } from '@/features/auth';
import { getApiUrl } from '@/config/api.config';
import i18n from '@/i18n';
import { errorCoordinator } from '@/services/errorCoordinator';

const http: AxiosInstance = axios.create({
  baseURL: getApiUrl(),
  withCredentials: true, // Required for HttpOnly cookies to be sent with requests
});

const SERVER_ERROR_STR = () => i18n.t('common:errors.unexpectedServerError');

http.interceptors.request.use(async (config) => {
  // Check for cross-tab logout before making request
  if (TokenService.checkCrossTabLogout()) {
    // Don't call removeTokens() here - let the useCrossTabAuth hook handle it
    throw new Error('Cross-tab logout detected');
  }

  // Read token from Zustand store (in-memory) instead of localStorage
  // This prevents race conditions when tokens are being written to storage
  const token = useAuthStore.getState().accessToken;

  console.log('🔍 [HTTP Interceptor] Reading token from Zustand state:', {
    hasToken: !!token,
    tokenLength: token?.length || 0,
    url: config.url
  });

  const newConfig = { ...config };

  if (token) {
    newConfig.headers.Authorization = `Bearer ${token}`;
  }

  if (!config.params) {
    newConfig.params = {};
  }

  return newConfig;
});

// Single-flight refresh promise to ensure only one refresh call at a time
let refreshPromise: Promise<string> | null = null;

http.interceptors.response.use(
  (res) => {
    // Signal recovery to ErrorCoordinator for this endpoint
    const sourceKey = `${res.config.method?.toUpperCase()}:${res.config.url}`;
    errorCoordinator.handleRecovery(sourceKey);
    return res;
  },
  async (err) => {
    const originalConfig = err.config;

    // Handle authentication failures
    if (err.response && err.response.status === 401) {
      // Check if this is a cross-tab logout
      if (TokenService.checkCrossTabLogout()) {
        // Don't call removeTokens() here - let the useCrossTabAuth hook handle it
        throw new Error('Cross-tab logout detected');
      }

      // Skip refresh for login/register/refresh endpoints to avoid infinite loops
      if (
        originalConfig.url?.includes('/auth/login') ||
        originalConfig.url?.includes('/auth/register') ||
        originalConfig.url?.includes('/auth/refresh')
      ) {
        throw err;
      }

      // Only retry once per request
      if (!originalConfig._retry) {
        originalConfig._retry = true;

        try {
          // Use single-flight pattern: if refresh is already in progress, wait for it
          if (!refreshPromise) {
            refreshPromise = TokenService.withRefreshLock(async () => {
              try {
                // Call refresh endpoint (uses HttpOnly cookie)
                const response = await http.post('/auth/refresh');
                const { accessToken } = response.data;

                // ✅ CRITICAL: Update both localStorage AND Zustand state
                // This ensures the interceptor reads the new token, not the old one
                const authStore = useAuthStore.getState();
                authStore.updateTokens(accessToken);

                console.log('🔄 [HTTP Interceptor] Token refreshed and Zustand state updated');

                return accessToken;
              } catch (refreshError: any) {
                console.error('❌ [HTTP Interceptor] Token refresh failed:', refreshError);

                // Check if it's an authentication error (expired refresh token)
                const isAuthError = refreshError?.response?.status === 401 ||
                                   refreshError?.response?.status === 403;

                if (isAuthError) {
                  console.log('🔐 [HTTP Interceptor] Refresh token expired or invalid - logging out');
                } else {
                  console.error('🌐 [HTTP Interceptor] Refresh failed due to network/server error:',
                    refreshError?.response?.status || 'network error');
                }

                // Refresh failed, clear tokens and auth state
                TokenService.removeTokens();
                const authStore = useAuthStore.getState();
                authStore.clearAuth();

                // Redirect to login page (only for auth errors, not network errors)
                if (window.location.pathname !== '/login') {
                  if (isAuthError) {
                    console.log('🚪 [HTTP Interceptor] Redirecting to login - session expired');
                    window.location.href = '/login?reason=session_expired';
                  } else {
                    console.log('🚪 [HTTP Interceptor] Redirecting to login - refresh failed');
                    window.location.href = '/login?reason=refresh_failed';
                  }
                }

                throw refreshError;
              } finally {
                // Clear refresh promise so next 401 can trigger new refresh
                refreshPromise = null;
              }
            });
          }

          // Wait for refresh to complete
          const newAccessToken = await refreshPromise;

          // Update Authorization header with new token
          originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;

          // Retry original request with new token
          return http(originalConfig);
        } catch (refreshError) {
          // Refresh promise failed - this is a safety net
          // The inner catch already handled logout/redirect, so just reject
          console.error('❌ [HTTP Interceptor] Outer catch - refresh promise failed:', refreshError);

          // Return a rejected promise to stop the request chain
          return Promise.reject(new Error(i18n.t('common:errors.sessionExpired')));
        }
      }
    }

    // Handle other authentication-related errors
    if (
      err.response &&
      (err.response.status === 403 || err.response.status === 419)
    ) {
      // Check if this is a cross-tab logout
      if (TokenService.checkCrossTabLogout()) {
        // Don't call removeTokens() here - let the useCrossTabAuth hook handle it
        throw new Error('Cross-tab logout detected');
      }
    }

    throw err;
  }
);

export { http, SERVER_ERROR_STR };
